import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, CalendarCheck2, CheckCheck, ClipboardCheck, ClipboardList, FileText, Footprints, History, IndianRupee, MessageSquareWarning, PartyPopper, PencilLine, Plane, ShieldAlert, Target, TimerOff, Trash2, UserX, Wallet } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Small type icon so alert kinds are recognisable at a glance in the list.
const TYPE_ICON = {
  target_alert: { icon: Target, cls: "text-amber-700 bg-amber-100" },
  target_proposal: { icon: ClipboardCheck, cls: "text-sky-700 bg-sky-100" },
  idle_alert: { icon: TimerOff, cls: "text-rose-700 bg-rose-100" },
  absent_alert: { icon: UserX, cls: "text-rose-700 bg-rose-100" },
  zero_activity: { icon: TimerOff, cls: "text-rose-700 bg-rose-100" },
  platform_cleanup: { icon: Trash2, cls: "text-slate-700 bg-slate-100" },
  rejection: { icon: ShieldAlert, cls: "text-rose-700 bg-rose-100" },
  supervisor_visit: { icon: Footprints, cls: "text-sky-700 bg-sky-100" },
  payment: { icon: IndianRupee, cls: "text-emerald-700 bg-emerald-100" },
  assignment: { icon: ClipboardList, cls: "text-sky-700 bg-sky-100" },
  grievance: { icon: MessageSquareWarning, cls: "text-amber-700 bg-amber-100" },
  application: { icon: FileText, cls: "text-slate-700 bg-slate-100" },
  survey: { icon: FileText, cls: "text-slate-700 bg-slate-100" },
  reassessment: { icon: FileText, cls: "text-slate-700 bg-slate-100" },
  pending_docs: { icon: FileText, cls: "text-amber-700 bg-amber-100" },
  gateway_balance: { icon: Wallet, cls: "text-rose-700 bg-rose-100" },
  attendance: { icon: CalendarCheck2, cls: "text-amber-700 bg-amber-100" },      // Phase 168
  leave_request: { icon: Plane, cls: "text-violet-700 bg-violet-100" },
  holiday: { icon: PartyPopper, cls: "text-sky-700 bg-sky-100" },
  demand_corrected: { icon: PencilLine, cls: "text-amber-700 bg-amber-100" },   // Phase 189 (citizen bell)
};
const typeIcon = (t) => TYPE_ICON[t] || { icon: Bell, cls: "text-muted-foreground bg-muted" };

/**
 * Where "View all" should go: the portal the user is currently inside (an admin may be browsing the
 * supervisor console), falling back to the portal of their role.
 */
export function notificationHistoryPath(code, pathname, role) {
  if (!code) return "/super/notifications"; // platform (Super Admin) bell
  const seg = (pathname || "").split("/")[2];
  if (seg === "portal" || role === "citizen") return `/${code}/portal/alerts`; // Phase 189: citizens land on My Alerts
  const portal = ["admin", "supervisor", "collector"].includes(seg)
    ? seg
    : role === "supervisor" ? "supervisor" : role === "tax_collector" ? "collector" : "admin";
  return `/${code}/${portal}/notification-history`;
}

export const NotificationsBell = ({ code }) => {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();

  // Phase 123: without a municipality code the bell serves the platform (Super Admin) alerts
  const base = code ? `/t/${code}/notifications` : "/platform/notifications";

  const load = async () => {
    try {
      const r = await api.get(base);
      setData(r.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [code]);

  const markAll = async () => {
    await api.put(`${base}/read-all`);
    load();
  };

  const viewAll = () => {
    setOpen(false);
    navigate(notificationHistoryPath(code, location.pathname, auth?.user?.role));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-white text-[10px] flex items-center justify-center">
              {data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 flex flex-col" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between px-3 py-2 border-b shrink-0">
          <p className="font-medium text-sm flex items-center gap-1.5 min-w-0">
            Notifications
            {data.notifications.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground tabular-nums whitespace-nowrap" data-testid="notifications-count"
                title={`${data.unread} unread of ${data.notifications.length}`}>
                ({data.notifications.length})
              </span>
            )}
          </p>
          {data.unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} data-testid="notifications-mark-all">
              <CheckCheck className="h-4 w-4 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        {/* Plain overflow container: Radix ScrollArea with max-h clipped the list because its viewport
            is h-full of an auto-height root, so items past ~320px were unreachable (Phase 120 fix). */}
        <div className="max-h-[min(24rem,60vh)] overflow-y-auto overscroll-contain [scrollbar-width:thin]"
          data-testid="notifications-list">
          {data.notifications.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="notifications-empty">No notifications</p>
          )}
          {data.notifications.map((n) => {
            const { icon: Icon, cls } = typeIcon(n.type);
            return (
              <div key={n.id} className={`px-3 py-2.5 border-b last:border-0 flex gap-2.5 ${!n.read_at ? "bg-accent/40" : ""}`}
                data-testid={`notification-item-${n.type || "generic"}`}>
                <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${cls}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.content}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{fmtDate(n.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t shrink-0">
          <Button variant="ghost" className="w-full h-9 rounded-none rounded-b-md text-xs font-medium text-primary"
            onClick={viewAll} data-testid="notifications-view-all">
            <History className="h-3.5 w-3.5 mr-1.5" /> {code ? "View all notifications" : "Open notifications settings"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
